/**
 * プレイヤー詳細のドロワー（連携・stats・所持・実績・BAN / 解除・データのエクスポート / 削除）。
 * Players 画面と Payments 画面（購入者のリンク）の両方から開くので components に置く。
 * base = `…/environments/:env/players`（dashboard モジュールの API）。
 * 所持品・実績の欄は最初の公開で見せない（launchHidden.ts・founder 2026-09-27）。BAN / 解除は残す（§1c の例外）。
 */
import { useState } from 'react';
import { api, ApiError, type PlayerDetail } from '../api';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { isLaunchHidden, isPartHidden } from '../launchHidden';
import { ago, ConfirmDialog, CopyButton, Drawer, ErrorState, fullDate, num, shortId, Spinner } from './ui';
import { OwnerOnly } from './OwnerOnly';

export function PlayerDrawer({ base, playerId, env, onClose, onChanged }: { base: string; playerId: string | null; env: 'development' | 'production'; onClose: () => void; onChanged: () => void }) {
  const { t, tr } = useT();
  const detail = useResource<{ player: PlayerDetail }>(playerId ? `${base}/${playerId}` : null);
  const [confirm, setConfirm] = useState<'ban' | 'unban' | 'delete' | null>(null);
  const [reason, setReason] = useState('');
  // 削除の強い確認: 短縮 ID（先頭 8 文字）を打たせる。全 36 文字は打たせない（コピペで素通りする長さより、
  // 目で見て打つ短さの方が「どのプレイヤーを消すか」を確かめさせられる）。API には完全な ID を confirm として送る
  const [typedId, setTypedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const p = detail.data?.player;

  /**
   * エクスポート: API（開発者セッション付き）から JSON を取り、ブラウザでファイルとして保存させる。
   * <a href> で直接開かないのは、認証がヘッダ（Authorization）で Cookie を使わないため（URL だけでは取れない）
   */
  const exportData = async () => {
    if (!p) return;
    setExporting(true);
    setNotice(null);
    setErr(null);
    try {
      const data = await api<unknown>(`${base}/${p.id}/export`);
      const file = `kumo-player-${p.id}.json`;
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file;
      a.click();
      // クリック直後に revoke すると一部ブラウザで保存が始まらないので、少し待ってから解放する
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setNotice(t('player.exported', { file }));
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setExporting(false);
    }
  };

  const act = async () => {
    if (!p || !confirm) return;
    setBusy(true);
    setErr(null);
    try {
      if (confirm === 'delete') {
        await api(`${base}/${p.id}/delete`, { method: 'POST', body: { confirm: p.id } });
        setConfirm(null);
        setTypedId('');
        // 消えたプレイヤーの詳細を開いたままにしない（再読み込みすると 404 になる）。一覧も更新させる
        onChanged();
        onClose();
        return;
      }
      await api(`${base}/${p.id}/${confirm}`, { method: 'POST', body: confirm === 'ban' ? { reason: reason.trim() || undefined } : {} });
      setConfirm(null);
      setReason('');
      detail.reload();
      onChanged();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const invEntries = p ? [...Object.entries(p.inventory.currencies), ...Object.entries(p.inventory.items)] : [];
  return (
    <>
      <Drawer open={!!playerId} title={p?.displayName ?? t('player.fallbackTitle')} onClose={onClose}>
        {detail.loading && !p && <Spinner />}
        {detail.error && <ErrorState error={detail.error} what={t('player.fallbackTitle')} />}
        {p && (
          <div className="detail">
            {p.banned && (
              <div className="alert alert-error">
                <strong>{t('player.bannedAgo', { ago: ago(p.bannedAt) })}</strong>
                <span>{p.banReason ?? t('player.noReason')}</span>
              </div>
            )}
            <dl className="kv">
              <dt>{t('player.id')}</dt>
              <dd className="mono">
                {p.id} <CopyButton text={p.id} />
              </dd>
              <dt>{t('player.account')}</dt>
              <dd>{p.isGuest ? t('player.guestAccount') : t('player.linkedAccount')}</dd>
              <dt>{t('player.joined')}</dt>
              <dd title={fullDate(p.createdAt)}>{ago(p.createdAt)}</dd>
              <dt>{t('player.lastSeen')}</dt>
              <dd title={fullDate(p.lastSeenAt)}>{ago(p.lastSeenAt)}</dd>
              <dt>{t('player.sessions')}</dt>
              <dd>{num(p.activeSessions)}</dd>
            </dl>

            <h3 className="section-title">{t('player.signIn')}</h3>
            <ul className="chips">
              {p.identities.map((i, idx) => (
                <li key={idx} className="chip">
                  <strong>{i.provider === 'guest' || i.provider === 'email' ? t(`player.provider.${i.provider}`) : i.provider}</strong>
                  {i.email && <span>{i.email}</span>}
                </li>
              ))}
            </ul>

            <h3 className="section-title">{t('player.stats')}</h3>
            {Object.keys(p.stats).length ? (
              <dl className="kv kv-compact">
                {Object.entries(p.stats).map(([k, v]) => (
                  <div key={k} className="kv-row">
                    <dt className="mono">{k}</dt>
                    <dd>{num(v)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="muted small">{t('player.noStats')}</p>
            )}

            {/* 他人の所持品は見せない（案 A は本人が読む・使うだけ = launchHidden.ts LAUNCH_HIDDEN_PARTS） */}
            {!isPartHidden('inventory.admin') && (
              <>
                <h3 className="section-title">{t('player.inventory')}</h3>
                {invEntries.length ? (
                  <ul className="chips">
                    {invEntries.map(([k, v]) => (
                      <li key={k} className="chip">
                        <span className="mono">{k}</span>
                        <strong>{num(v)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted small">{t('player.noInventory')}</p>
                )}
              </>
            )}

            {!isLaunchHidden('achievements') && (
              <>
                <h3 className="section-title">{t('player.achievements')}</h3>
                {p.achievements.length ? (
                  <ul className="chips">
                    {p.achievements.map((a) => (
                      <li key={`${a.key}|${a.period}`} className="chip" title={fullDate(a.unlockedAt)}>
                        <span className="mono">{a.key}</span>
                        {a.period !== 'all' && <span className="muted">{a.period}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted small">{t('player.noAchievements')}</p>
                )}
              </>
            )}

            <h3 className="section-title">{t('player.privacy')}</h3>
            <p className="muted small">{t('player.privacyNote')}</p>
            {notice && (
              <p className="small" role="status">
                {notice}
              </p>
            )}
            {err && !confirm && (
              <p className="alert alert-error" role="alert">
                {err}
              </p>
            )}

            <div className="detail-actions">
              {/* 書き出し（個人情報の持ち出し）・削除は持ち主だけ（2026-10-22 core/handoff-1b・サーバーの表 lib/projectAccess.ts）。BAN / 解除は共同作業者も */}
              <OwnerOnly>
                <button type="button" className="btn btn-ghost" onClick={() => void exportData()} disabled={exporting}>
                  {exporting ? t('player.exporting') : t('player.exportData')}
                </button>
                <button type="button" className="btn btn-danger" onClick={() => setConfirm('delete')}>
                  {t('player.delete')}
                </button>
              </OwnerOnly>
              {p.banned ? (
                <button type="button" className="btn btn-ghost" onClick={() => setConfirm('unban')}>
                  {t('player.unban')}
                </button>
              ) : (
                <button type="button" className="btn btn-danger" onClick={() => setConfirm('ban')}>
                  {t('player.ban')}
                </button>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirm !== null}
        title={t(confirm === 'ban' ? 'player.banTitle' : confirm === 'delete' ? 'player.deleteTitle' : 'player.unbanTitle', {
          name: p?.displayName ?? t('player.thisPlayer')
        })}
        confirmLabel={confirm === 'ban' ? t('player.ban') : confirm === 'delete' ? t('player.deleteConfirm') : t('player.unbanConfirm')}
        tone={confirm === 'ban' || confirm === 'delete' ? 'danger' : 'default'}
        busy={busy}
        confirmDisabled={confirm === 'delete' && (!p || typedId.trim() !== shortId(p.id))}
        onClose={() => {
          setConfirm(null);
          setTypedId('');
          setErr(null);
        }}
        onConfirm={() => void act()}
      >
        {confirm === 'delete' && p ? (
          <>
            <p>{tr('player.deleteBody', { env: <strong>{t(`env.inline.${env}`)}</strong> })}</p>
            {/* 課金（payments）を閉じている間は購入が無い = 購入の記録の話をしない（2026-09-30 console/words-pass） */}
            {!isLaunchHidden('payments') && <p className="muted small">{t('player.deletePurchases')}</p>}
            <label className="field">
              <span>{tr('player.deleteTypeId', { id: <code>{shortId(p.id)}</code> })}</span>
              <input value={typedId} onChange={(e) => setTypedId(e.target.value)} autoComplete="off" spellCheck={false} className="mono" />
            </label>
          </>
        ) : confirm === 'ban' ? (
          <>
            <p>{tr('player.banBody', { env: <strong>{t(`env.inline.${env}`)}</strong> })}</p>
            <label className="field">
              <span>
                {t('player.reason')} <small>{t('player.reasonNote')}</small>
              </span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder={t('player.reasonPlaceholder')} />
            </label>
          </>
        ) : (
          <p>{t('player.unbanBody')}</p>
        )}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
